import { ApiException, client } from "@/lib/client";
import { queryClient } from "@/lib/react-query";
import type { Service, ServiceStatus } from "@/shared/model/service";
import { useMutation, useQuery } from "@tanstack/react-query";

export const servicesQueryKey = "services";

export const useServicesController = () => {
  const { data, isLoading, error } = useQuery<Service[]>({
    queryKey: [servicesQueryKey],
    queryFn: async () => {
      const response = await client.fetch(`/services`);

      if (!response.ok) {
        throw new ApiException(response.statusText);
      }

      const json = await response.json();
      return json.data?.services || [];
    },
    refetchInterval: 30000, // Refetch every 30 seconds to keep status updated
  });

  const createService = useMutation({
    mutationFn: async ({ name, title, icon, status, notes }: { name: string; title?: string; icon?: string; status?: ServiceStatus; notes?: string }) => {
      const response = await client.fetch(`/services`, {
        method: "POST",
        body: JSON.stringify({ name, title, icon, status, notes }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new ApiException(errorData.message || response.statusText);
      }

      const json = await response.json();
      return json.data?.service;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [servicesQueryKey] });
    },
  });

  const updateService = useMutation({
    mutationFn: async ({ serviceId, name, title, icon, status, notes }: { serviceId: string; name?: string; title?: string; icon?: string; status?: ServiceStatus; notes?: string }) => {
      const response = await client.fetch(`/services/${serviceId}`, {
        method: "PUT",
        body: JSON.stringify({ name, title, icon, status, notes }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new ApiException(errorData.message || response.statusText);
      }

      const json = await response.json();
      return json.data?.service;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [servicesQueryKey] });
    },
  });

  const deleteService = useMutation({
    mutationFn: async (serviceId: string) => {
      const response = await client.fetch(`/services/${serviceId}`, {
        method: "DELETE",
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new ApiException(errorData.message || response.statusText);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [servicesQueryKey] });
    },
  });

  return {
    data: data || [],
    isLoading,
    error,
    createService,
    updateService,
    deleteService,
  };
};

export const useServiceController = (serviceId: string) => {
  const { data, isLoading, error } = useQuery<Service>({
    queryKey: [servicesQueryKey, serviceId],
    queryFn: async () => {
      const response = await client.fetch(`/services/${serviceId}`);

      if (!response.ok) {
        throw new ApiException(response.statusText);
      }

      const json = await response.json();
      return json.data?.service;
    },
    enabled: !!serviceId,
  });

  return {
    data,
    isLoading,
    error,
  };
};